import type {
  WorkflowNotificationListResponse,
  PushNotificationListResponse,
  PushNotification,
  CreatePushNotificationRequest,
  UpdatePushNotificationRequest,
} from "../types/notification";
import { apiFetch } from "./client";

const PUSH_BASE = "/api/v1/admin/push-notifications";
const NOTIF_BASE = "/api/v1/admin/notifications";

// -- Workflow notifications (Unit 25.1) -----------------------------------

export async function listWorkflowNotifications(
  page = 1,
  pageLimit = 10,
): Promise<WorkflowNotificationListResponse> {
  const params = new URLSearchParams({
    page: String(page),
    page_limit: String(pageLimit),
  });
  return apiFetch<WorkflowNotificationListResponse>(`${NOTIF_BASE}?${params}`);
}

export async function markNotificationsRead(): Promise<void> {
  await apiFetch<void>(`${NOTIF_BASE}/read`, { method: "PATCH" });
}

/**
 * Fetch unread notification count.
 *
 * Pass `{ background: true }` ONLY for caller-driven polling (e.g. the
 * 30s interval inside `useUnreadCount`). User-triggered refreshes (route
 * change, explicit reload) MUST omit the flag — otherwise the request
 * tells the backend to skip the sliding-window touch and the user gets
 * a premature 401 on the next idle check (HH-200 Unit 28).
 */
export async function getUnreadCount(
  opts: { background?: boolean } = {},
): Promise<number> {
  const headers = opts.background ? { "X-Activity": "background" } : undefined;
  const data = await apiFetch<{ unread_count: number }>(
    `${NOTIF_BASE}/unread-count`,
    headers ? { headers } : {},
  );
  return data.unread_count;
}

// -- Push notifications (Unit 25) -----------------------------------------

export async function listPushNotifications(
  page = 1,
  pageLimit = 10,
): Promise<PushNotificationListResponse> {
  const params = new URLSearchParams({
    page: String(page),
    page_limit: String(pageLimit),
  });
  return apiFetch<PushNotificationListResponse>(`${PUSH_BASE}?${params}`);
}

export async function createPushNotification(
  payload: CreatePushNotificationRequest,
): Promise<PushNotification> {
  return apiFetch<PushNotification>(PUSH_BASE, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updatePushNotification(
  id: string,
  payload: UpdatePushNotificationRequest,
): Promise<PushNotification> {
  return apiFetch<PushNotification>(`${PUSH_BASE}/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deletePushNotification(id: string): Promise<void> {
  await apiFetch<void>(`${PUSH_BASE}/${id}`, { method: "DELETE" });
}
